import classNames from "classnames";
import { isEmpty, isNil } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { isZoneSettled, ZONE_TYPES } from "../../field-status";
import { ProposalLine, ZoneRow } from "./zone-row";
import { GROUP_KINDS, SECTION_MODES } from "./zone-sections";
import {
  isPendingStatus,
  pendingProposalOf,
  SUMMARY_KINDS,
  zoneSummary,
} from "./zones";

const MIN_GROUP = 2;
const CONFIRM_FROM = 5;
const CONFIRM_DELAY = 4000;
const GROUP_ACTIONS = { FILL: "fill", LEAVE: "leave" };

export const EditorIntro = ({ onDismiss }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  return (
    <div className="dynamic-documents-editor__intro">
      <p>{t("ZONES.INTRO")}</p>
      <button
        type="button"
        className="dynamic-documents-editor__link"
        onClick={onDismiss}
      >
        {t("ZONES.INTRO_DISMISS")}
      </button>
    </div>
  );
};

export const SectionNote = ({ mode }) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  if (mode === SECTION_MODES.LOADING)
    return (
      <p className="dynamic-documents-editor__muted dynamic-documents-editor__zones-note">
        {t("ZONES.LOCATING")}
      </p>
    );
  if (mode === SECTION_MODES.DATA)
    return (
      <p className="dynamic-documents-editor__muted dynamic-documents-editor__zones-note">
        {t("ZONES.DATA_GROUPS_HINT")}
      </p>
    );
  return null;
};

const GroupLink = ({ text, confirm, confirming, onRun, onHover }) => (
  <button
    type="button"
    className={classNames("dynamic-documents-editor__link", {
      "dynamic-documents-editor__link--confirm": confirming,
    })}
    onMouseEnter={() => onHover?.(true)}
    onMouseLeave={() => onHover?.(false)}
    onFocus={() => onHover?.(true)}
    onBlur={() => onHover?.(false)}
    onClick={onRun}
  >
    {confirming ? confirm : text}
  </button>
);

const isLeftEmpty = (zone, mappings) =>
  zoneSummary(zone, mappings).kind === SUMMARY_KINDS.EMPTY;

const SettleGroupHead = ({
  group,
  unsettled,
  zoneById,
  mappings,
  proposals,
  searching,
  onFillGroup,
  onLeaveGroup,
  onGroupHover,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    if (isNil(confirming)) return undefined;
    const timer = setTimeout(() => setConfirming(null), CONFIRM_DELAY);
    return () => clearTimeout(timer);
  }, [confirming]);

  const fillable =
    !searching &&
    unsettled.length >= MIN_GROUP &&
    unsettled.every((zone) => !isNil(pendingProposalOf(zone, proposals)));
  const emptiable = group.zoneIds
    .map((id) => zoneById.get(id))
    .filter(
      (zone) =>
        !isNil(zone) &&
        zone.type === ZONE_TYPES.ANCHOR &&
        !isLeftEmpty(zone, mappings),
    );
  const filled = emptiable.filter((zone) =>
    isZoneSettled(zone, mappings),
  ).length;
  const leavable =
    emptiable.length >= MIN_GROUP &&
    emptiable.some((zone) => !isZoneSettled(zone, mappings));

  const run = ({ action, targets, apply, confirm }) => {
    if (confirm && confirming !== action) {
      setConfirming(action);
      return;
    }
    setConfirming(null);
    apply(targets);
  };

  return (
    <div className="dynamic-documents-editor__group-head" data-group-head>
      <span
        className="dynamic-documents-editor__group-title"
        title={group.title}
      >
        {group.title}
      </span>
      <span className="dynamic-documents-editor__group-meta">
        {group.kind === GROUP_KINDS.HEADING
          ? t("ZONES.GROUP_TODO_PAGE", {
              page: group.page + 1,
              count: unsettled.length,
            })
          : t("ZONES.GROUP_TODO", { count: unsettled.length })}
      </span>
      {(fillable || leavable) && (
        <span className="dynamic-documents-editor__group-actions">
          {fillable && (
            <GroupLink
              text={t("ZONES.GROUP_FILL_ALL")}
              confirm={t("ZONES.GROUP_CONFIRM_FILL", {
                count: unsettled.length,
              })}
              confirming={confirming === GROUP_ACTIONS.FILL}
              onHover={(hovering) =>
                onGroupHover(hovering ? unsettled.map((zone) => zone.id) : null)
              }
              onRun={() =>
                run({
                  action: GROUP_ACTIONS.FILL,
                  targets: unsettled,
                  apply: onFillGroup,
                  confirm: unsettled.length >= CONFIRM_FROM,
                })
              }
            />
          )}
          {leavable && (
            <GroupLink
              text={t("ZONES.GROUP_LEAVE_ALL")}
              confirm={
                filled > 0
                  ? t("ZONES.GROUP_CONFIRM_LEAVE_FILLED", {
                      total: emptiable.length,
                      count: filled,
                    })
                  : t("ZONES.GROUP_CONFIRM_LEAVE", { count: emptiable.length })
              }
              confirming={confirming === GROUP_ACTIONS.LEAVE}
              onRun={() =>
                run({
                  action: GROUP_ACTIONS.LEAVE,
                  targets: emptiable,
                  apply: onLeaveGroup,
                  confirm: filled > 0 || emptiable.length >= CONFIRM_FROM,
                })
              }
            />
          )}
        </span>
      )}
    </div>
  );
};

const SettleList = ({
  zones,
  allZones,
  openZone,
  leaving,
  total,
  groups,
  mode,
  placeOf,
  mappings,
  proposals,
  payload,
  samplePrefix,
  searching,
  pointerZoneId,
  statusOf,
  titleOf,
  renderPanel,
  onToggle,
  onFill,
  onOpen,
  onLeaveEmpty,
  onHover,
  onFocus,
  onBlur,
  onFillGroup,
  onLeaveGroup,
  onGroupHover,
  onSeeAll,
}) => {
  const { t } = useTranslation(
    NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS_EDITOR,
  );
  const leavingIds = useMemo(() => new Set(Object.keys(leaving)), [leaving]);
  const zoneById = useMemo(
    () => new Map(allZones.map((zone) => [zone.id, zone])),
    [allZones],
  );
  const unsettledIds = new Set(zones.map((zone) => zone.id));
  const rowIds = new Set([
    ...unsettledIds,
    ...leavingIds,
    ...(isNil(openZone) ? [] : [openZone.id]),
  ]);
  const shownGroups = groups
    .map((group) => {
      const rows = group.zoneIds
        .filter((id) => rowIds.has(id) && zoneById.has(id))
        .map((id) => zoneById.get(id));
      return {
        ...group,
        rows,
        unsettled: rows.filter(
          (zone) => unsettledIds.has(zone.id) && !leavingIds.has(zone.id),
        ),
      };
    })
    .filter(({ rows }) => !isEmpty(rows));

  const renderRow = (zone) => {
    const open = zone.id === openZone?.id;
    const before = leaving[zone.id];
    const status = before?.status ?? statusOf(zone);
    const leavingRow = !isNil(before);
    return (
      <ZoneRow
        key={zone.id}
        zone={zone}
        title={titleOf(zone)}
        status={status}
        place={placeOf.get(zone.id)}
        open={open}
        pointed={zone.id === pointerZoneId}
        leaving={leavingRow}
        line={
          isPendingStatus(status) && (
            <ProposalLine
              zone={zone}
              proposals={before?.proposals ?? proposals}
              payload={payload}
              samplePrefix={samplePrefix}
              searching={searching}
              onFill={onFill}
              onOpen={onOpen}
              onLeaveEmpty={onLeaveEmpty}
            />
          )
        }
        panel={open ? renderPanel(zone) : null}
        onToggle={onToggle}
        onHover={onHover}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  };

  return (
    <div className="dynamic-documents-editor__zones">
      <h2 className="dynamic-documents-editor__zones-title">
        {t("ZONES.TO_SETTLE", { count: zones.length })}
      </h2>
      <SectionNote mode={mode} />
      {shownGroups.map((group) => (
        <section
          key={group.key}
          className="dynamic-documents-editor__group"
          data-group={group.key}
        >
          {group.kind !== GROUP_KINDS.FLAT && (
            <SettleGroupHead
              group={group}
              unsettled={group.unsettled}
              zoneById={zoneById}
              mappings={mappings}
              proposals={proposals}
              searching={searching}
              onFillGroup={onFillGroup}
              onLeaveGroup={onLeaveGroup}
              onGroupHover={onGroupHover}
            />
          )}
          <ul className="dynamic-documents-editor__rows">
            {group.rows.map(renderRow)}
          </ul>
        </section>
      ))}
      <div className="dynamic-documents-editor__zones-footer">
        <button
          type="button"
          className="dynamic-documents-editor__link"
          onClick={onSeeAll}
        >
          {t("ZONES.SEE_ALL", { count: total })}
        </button>
      </div>
    </div>
  );
};

export default SettleList;
